"use client";

import { c as _c } from "react/compiler-runtime";
import { Badge, Label } from "../ui";
import { RiCalendarLine, RiCheckboxCircleLine, RiGitBranchLine, RiMessageLine } from "react-icons/ri";
import IdDisplay from "@/components/ui/id-display";
import { Skeleton } from "@/components/ui/skeleton";
import { formatToHumanReadable } from "@/lib/format-data";
import { AppointmentStepDetail } from "./appointment-step-detail";
import { useCarePlanContext } from "./care-plan";
import { CycleBadge } from "./cycle-badge";
import { DecisionStepDetail } from "./decision/decision-step-detail";
import { MessageStepDetail } from "./message-step-detail";
import { StepDate } from "./step-date";
import { StepStatusSelector } from "./step-status-selector";
import { TaskStepDetail } from "./task-step-detail";
import { StepType, getStepDisplayName, isPatientStep } from "./types";
export function StepDetail() {
  const $ = _c(44);
  const {
    state: t0,
    config: t1,
    lookups: t2,
    cycles: t3
  } = useCarePlanContext();
  const {
    selectedStep
  } = t0;
  const {
    readOnly
  } = t1;
  const {
    serviceById,
    medicationById,
    measurementById,
    isLookupsLoading
  } = t2;
  const {
    cycles: allCycles,
    cycleColorMap,
    cycleToParentPass
  } = t3;
  if (!selectedStep) {
    return null;
  }
  const effectiveCycleId = "cycleId" in selectedStep ? selectedStep.cycleId as string | null : null;
  const effectiveCyclePass = "cyclePass" in selectedStep && typeof selectedStep.cyclePass === "number" ? selectedStep.cyclePass : effectiveCycleId ? 1 : undefined;
  let t4;
  if ($[0] !== selectedStep.type) {
    t4 = selectedStep.type === StepType.MESSAGE ? <MessageStepDetail /> : selectedStep.type === StepType.TASK ? <TaskStepDetail /> : selectedStep.type === StepType.DECISION ? <DecisionStepDetail /> : <AppointmentStepDetail />;
    $[0] = selectedStep.type;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const detail = t4;
  let t5;
  if ($[2] !== selectedStep.type) {
    t5 = selectedStep.type === StepType.MESSAGE ? <RiMessageLine size={14} /> : selectedStep.type === StepType.TASK ? <RiCheckboxCircleLine size={14} /> : selectedStep.type === StepType.DECISION ? <RiGitBranchLine size={14} /> : <RiCalendarLine size={14} />;
    $[2] = selectedStep.type;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const typeIcon = t5;
  const typeLabel = selectedStep.type === StepType.MESSAGE ? "Message" : selectedStep.type === StepType.TASK ? "Task" : selectedStep.type === StepType.DECISION ? "Decision" : "Appointment";
  const hasLookupDependentContent = selectedStep.serviceIds.length > 0 || selectedStep.medicationIds.length > 0 || selectedStep.measurements.length > 0;
  const showNameSkeleton = isLookupsLoading && selectedStep.type !== StepType.MESSAGE && hasLookupDependentContent;
  let t6;
  if ($[4] !== measurementById || $[5] !== medicationById || $[6] !== selectedStep || $[7] !== serviceById) {
    t6 = getStepDisplayName(selectedStep, {
      serviceById,
      medicationById,
      measurementById
    });
    $[4] = measurementById;
    $[5] = medicationById;
    $[6] = selectedStep;
    $[7] = serviceById;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const displayName = t6;
  let t7;
  if ($[9] !== typeIcon) {
    t7 = <span className="flex items-center justify-center rounded-md bg-primary p-1 text-primary-foreground">{typeIcon}</span>;
    $[9] = typeIcon;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== displayName || $[12] !== showNameSkeleton) {
    t8 = showNameSkeleton ? <Skeleton className="h-4 w-40 mb-1" /> : <span className="text-body-strong text-foreground truncate block">{displayName || "Untitled Step"}</span>;
    $[11] = displayName;
    $[12] = showNameSkeleton;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== typeLabel) {
    t9 = <span className="text-caption text-foreground-secondary">{typeLabel}</span>;
    $[14] = typeLabel;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <div className="w-full overflow-hidden">{t8}{t9}</div>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7) {
    t11 = <div className="flex items-center gap-2 overflow-hidden">{t7}{t10}</div>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== selectedStep.id) {
    t12 = <IdDisplay id={selectedStep.id} label="Step ID" className="text-tiny text-foreground-muted" />;
    $[22] = selectedStep.id;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <div className="max-w-96">{t11}{t12}</div>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== allCycles || $[28] !== cycleColorMap || $[29] !== cycleToParentPass || $[30] !== effectiveCycleId || $[31] !== effectiveCyclePass) {
    t14 = effectiveCycleId && effectiveCyclePass !== undefined && (() => {
      const ancestors = [];
      let curId = effectiveCycleId;
      let cur = allCycles.find(c => c.id === curId);
      while (cur?.parentCycleId) {
        ancestors.unshift({
          cycleId: cur.parentCycleId,
          pass: cycleToParentPass.get(curId) ?? 1
        });
        curId = cur.parentCycleId;
        cur = allCycles.find(c_0 => c_0.id === curId);
      }
      return <div className="mt-2 flex flex-wrap items-center gap-1">{ancestors.map(t15 => {
          const {
            cycleId: aid,
            pass
          } = t15;
          const idx = cycleColorMap.get(aid);
          return idx !== undefined ? <CycleBadge key={aid} cyclePass={pass} colorIndex={idx} cycleName={allCycles.find(c_1 => c_1.id === aid)?.name} /> : null;
        })}<CycleBadge cyclePass={effectiveCyclePass} colorIndex={cycleColorMap.get(effectiveCycleId) ?? 0} cycleName={allCycles.find(c_2 => c_2.id === effectiveCycleId)?.name} /></div>;
    })();
    $[27] = allCycles;
    $[28] = cycleColorMap;
    $[29] = cycleToParentPass;
    $[30] = effectiveCycleId;
    $[31] = effectiveCyclePass;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== selectedStep.id) {
    t15 = <StepDate key={selectedStep.id} />;
    $[33] = selectedStep.id;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== readOnly || $[36] !== selectedStep) {
    t16 = readOnly ? isPatientStep(selectedStep) ? <div><Label>Status</Label><Badge>{formatToHumanReadable(selectedStep.status)}</Badge></div> : null : <StepStatusSelector />;
    $[35] = readOnly;
    $[36] = selectedStep;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== detail || $[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t16) {
    t17 = <div className="space-y-4">{t13}{t14}{t15}{detail}{t16}</div>;
    $[38] = detail;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
}